<template>
  <IntercomLayout>
    <!-- Topbar -->
    <template #topbar>
      <IntercomTopbar
        :show-search="false"
        app-name="OnePlaceHQ"
        product-name="Billing"
        :business-name="businessName"
        :user="user"
        @logout="handleLogout"
      />
    </template>

    <!-- Sidebar -->
    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <!-- Main Content -->
    <div class="reports-view">
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h1 class="page-title">Reports</h1>
          <p class="page-subtitle">Sales analytics, purchase reports, tax compliance, and outstanding tracking</p>
        </div>
      </div>

      <!-- Tab Groups -->
      <div class="tab-groups">
        <div class="tab-group">
          <span class="tab-group-label">Sales</span>
          <div class="report-tabs">
            <button
              v-for="tab in salesTabs"
              :key="tab.id"
              class="tab-btn"
              :class="{ active: activeTab === tab.id }"
              @click="activeTab = tab.id"
            >
              {{ tab.label }}
            </button>
          </div>
        </div>
        <div class="tab-group">
          <span class="tab-group-label">Purchases</span>
          <div class="report-tabs">
            <button
              v-for="tab in purchaseTabs"
              :key="tab.id"
              class="tab-btn"
              :class="{ active: activeTab === tab.id }"
              @click="activeTab = tab.id"
            >
              {{ tab.label }}
            </button>
          </div>
        </div>
        <div class="tab-group">
          <span class="tab-group-label">GST</span>
          <div class="report-tabs">
            <button
              v-for="tab in gstTabs"
              :key="tab.id"
              class="tab-btn"
              :class="{ active: activeTab === tab.id }"
              @click="activeTab = tab.id"
            >
              {{ tab.label }}
            </button>
          </div>
        </div>
        <div class="tab-group">
          <span class="tab-group-label">Outstanding</span>
          <div class="report-tabs">
            <button
              class="tab-btn"
              :class="{ active: activeTab === 'outstanding' }"
              @click="activeTab = 'outstanding'"
            >
              Receivables
            </button>
            <button
              class="tab-btn"
              :class="{ active: activeTab === 'payables' }"
              @click="activeTab = 'payables'"
            >
              Payables
            </button>
          </div>
        </div>
      </div>

      <!-- Sales Summary Tab -->
      <div v-if="activeTab === 'sales-summary'">
        <IntercomCard>
          <div class="report-controls">
            <div class="date-range">
              <label>From</label>
              <IntercomDateInput v-model="fromDate" />
              <label>To</label>
              <IntercomDateInput v-model="toDate" />
              <IntercomButton variant="primary" :loading="isTabLoading('sales-summary')" @click="fetchSalesSummary">
                Generate
              </IntercomButton>
            </div>
          </div>

          <div v-if="isTabLoading('sales-summary') && !salesSummary" class="loading-state">Loading...</div>

          <div v-if="salesSummary" class="summary-grid">
            <div class="summary-card highlight">
              <div class="summary-label">Total Sales</div>
              <div class="summary-value">{{ formatCurrency(salesSummary.total_sales) }}</div>
            </div>
            <div class="summary-card">
              <div class="summary-label">Taxable Amount</div>
              <div class="summary-value">{{ formatCurrency(salesSummary.total_taxable) }}</div>
            </div>
            <div class="summary-card">
              <div class="summary-label">Total Tax</div>
              <div class="summary-value">{{ formatCurrency(salesSummary.total_tax) }}</div>
            </div>
            <div class="summary-card">
              <div class="summary-label">Discount</div>
              <div class="summary-value">{{ formatCurrency(salesSummary.total_discount) }}</div>
            </div>
            <div class="summary-card">
              <div class="summary-label">Invoices</div>
              <div class="summary-value">{{ salesSummary.invoice_count }}</div>
            </div>
          </div>

          <!-- Payment Mode Breakdown -->
          <div v-if="salesSummary && salesSummary.payment_breakdown && salesSummary.payment_breakdown.length > 0" class="payment-breakdown">
            <h3 class="section-title">Payment Mode Breakdown</h3>
            <IntercomTable
              :columns="paymentBreakdownColumns"
              :data="salesSummary.payment_breakdown"
            />
          </div>
        </IntercomCard>
      </div>

      <!-- Product Sales Tab -->
      <div v-if="activeTab === 'product-sales'">
        <IntercomCard>
          <div class="report-controls">
            <div class="date-range">
              <label>From</label>
              <IntercomDateInput v-model="fromDate" />
              <label>To</label>
              <IntercomDateInput v-model="toDate" />
              <IntercomButton variant="primary" :loading="isTabLoading('product-sales')" @click="fetchProductSales">
                Generate
              </IntercomButton>
            </div>
          </div>

          <div v-if="isTabLoading('product-sales') && productSalesRows.length === 0" class="loading-state">Loading...</div>
          <IntercomTable
            v-else-if="productSalesRows.length > 0"
            :columns="productSalesColumns"
            :data="productSalesRows"
          />
          <div v-else-if="fetchedTabs.has('product-sales')" class="empty-state">
            No product sales in this period
          </div>
        </IntercomCard>
      </div>

      <!-- Customer Sales Tab -->
      <div v-if="activeTab === 'customer-sales'">
        <IntercomCard>
          <div class="report-controls">
            <div class="date-range">
              <label>From</label>
              <IntercomDateInput v-model="fromDate" />
              <label>To</label>
              <IntercomDateInput v-model="toDate" />
              <IntercomButton variant="primary" :loading="isTabLoading('customer-sales')" @click="fetchCustomerSales">
                Generate
              </IntercomButton>
            </div>
          </div>

          <div v-if="isTabLoading('customer-sales') && customerSalesRows.length === 0" class="loading-state">Loading...</div>
          <IntercomTable
            v-else-if="customerSalesRows.length > 0"
            :columns="customerSalesColumns"
            :data="customerSalesRows"
          />
          <div v-else-if="fetchedTabs.has('customer-sales')" class="empty-state">
            No customer sales in this period
          </div>
        </IntercomCard>
      </div>

      <!-- Daily Sales Tab -->
      <div v-if="activeTab === 'daily-sales'">
        <IntercomCard>
          <div class="report-controls">
            <div class="date-range">
              <label>From</label>
              <IntercomDateInput v-model="fromDate" />
              <label>To</label>
              <IntercomDateInput v-model="toDate" />
              <IntercomButton variant="primary" :loading="isTabLoading('daily-sales')" @click="fetchDailySales">
                Generate
              </IntercomButton>
            </div>
          </div>

          <div v-if="isTabLoading('daily-sales') && dailySalesRows.length === 0" class="loading-state">Loading...</div>
          <IntercomTable
            v-else-if="dailySalesRows.length > 0"
            :columns="dailySalesColumns"
            :data="dailySalesRows"
          />
          <div v-else-if="fetchedTabs.has('daily-sales')" class="empty-state">
            No sales in this period
          </div>
        </IntercomCard>
      </div>

      <!-- Purchase Summary Tab -->
      <div v-if="activeTab === 'purchase-summary'">
        <IntercomCard>
          <div class="report-controls">
            <div class="date-range">
              <label>From</label>
              <IntercomDateInput v-model="fromDate" />
              <label>To</label>
              <IntercomDateInput v-model="toDate" />
              <IntercomButton variant="primary" :loading="isTabLoading('purchase-summary')" @click="fetchPurchaseSummary">
                Generate
              </IntercomButton>
            </div>
          </div>

          <div v-if="isTabLoading('purchase-summary') && !purchaseSummary" class="loading-state">Loading...</div>

          <div v-if="purchaseSummary" class="summary-grid">
            <div class="summary-card highlight">
              <div class="summary-label">Total Purchases</div>
              <div class="summary-value">{{ formatCurrency(purchaseSummary.total_purchases) }}</div>
            </div>
            <div class="summary-card">
              <div class="summary-label">Taxable Amount</div>
              <div class="summary-value">{{ formatCurrency(purchaseSummary.total_taxable) }}</div>
            </div>
            <div class="summary-card">
              <div class="summary-label">CGST</div>
              <div class="summary-value cgst">{{ formatCurrency(purchaseSummary.total_cgst) }}</div>
            </div>
            <div class="summary-card">
              <div class="summary-label">SGST</div>
              <div class="summary-value sgst">{{ formatCurrency(purchaseSummary.total_sgst) }}</div>
            </div>
            <div class="summary-card" v-if="purchaseSummary.total_igst > 0">
              <div class="summary-label">IGST</div>
              <div class="summary-value igst">{{ formatCurrency(purchaseSummary.total_igst) }}</div>
            </div>
            <div class="summary-card">
              <div class="summary-label">Total Tax</div>
              <div class="summary-value">{{ formatCurrency(purchaseSummary.total_tax) }}</div>
            </div>
            <div class="summary-card">
              <div class="summary-label">Bills</div>
              <div class="summary-value">{{ purchaseSummary.bill_count }}</div>
            </div>
          </div>
        </IntercomCard>
      </div>

      <!-- Vendor Purchases Tab -->
      <div v-if="activeTab === 'vendor-purchases'">
        <IntercomCard>
          <div class="report-controls">
            <div class="date-range">
              <label>From</label>
              <IntercomDateInput v-model="fromDate" />
              <label>To</label>
              <IntercomDateInput v-model="toDate" />
              <IntercomButton variant="primary" :loading="isTabLoading('vendor-purchases')" @click="fetchVendorPurchases">
                Generate
              </IntercomButton>
            </div>
          </div>

          <div v-if="isTabLoading('vendor-purchases') && vendorPurchaseRows.length === 0" class="loading-state">Loading...</div>
          <IntercomTable
            v-else-if="vendorPurchaseRows.length > 0"
            :columns="vendorPurchaseColumns"
            :data="vendorPurchaseRows"
          />
          <div v-else-if="fetchedTabs.has('vendor-purchases')" class="empty-state">
            No vendor purchases in this period
          </div>
        </IntercomCard>
      </div>

      <!-- Product Purchases Tab -->
      <div v-if="activeTab === 'product-purchases'">
        <IntercomCard>
          <div class="report-controls">
            <div class="date-range">
              <label>From</label>
              <IntercomDateInput v-model="fromDate" />
              <label>To</label>
              <IntercomDateInput v-model="toDate" />
              <IntercomButton variant="primary" :loading="isTabLoading('product-purchases')" @click="fetchProductPurchases">
                Generate
              </IntercomButton>
            </div>
          </div>

          <div v-if="isTabLoading('product-purchases') && productPurchaseRows.length === 0" class="loading-state">Loading...</div>
          <IntercomTable
            v-else-if="productPurchaseRows.length > 0"
            :columns="productPurchaseColumns"
            :data="productPurchaseRows"
          />
          <div v-else-if="fetchedTabs.has('product-purchases')" class="empty-state">
            No product purchases in this period
          </div>
        </IntercomCard>
      </div>

      <!-- Daily Purchases Tab -->
      <div v-if="activeTab === 'daily-purchases'">
        <IntercomCard>
          <div class="report-controls">
            <div class="date-range">
              <label>From</label>
              <IntercomDateInput v-model="fromDate" />
              <label>To</label>
              <IntercomDateInput v-model="toDate" />
              <IntercomButton variant="primary" :loading="isTabLoading('daily-purchases')" @click="fetchDailyPurchases">
                Generate
              </IntercomButton>
            </div>
          </div>

          <div v-if="isTabLoading('daily-purchases') && dailyPurchaseRows.length === 0" class="loading-state">Loading...</div>
          <IntercomTable
            v-else-if="dailyPurchaseRows.length > 0"
            :columns="dailyPurchaseColumns"
            :data="dailyPurchaseRows"
          />
          <div v-else-if="fetchedTabs.has('daily-purchases')" class="empty-state">
            No purchases in this period
          </div>
        </IntercomCard>
      </div>

      <!-- Outstanding Tab -->
      <div v-if="activeTab === 'outstanding'">
        <IntercomCard>
          <div class="report-controls">
            <IntercomButton variant="primary" :loading="isTabLoading('outstanding')" @click="fetchOutstanding">
              Refresh
            </IntercomButton>
          </div>

          <div v-if="isTabLoading('outstanding') && !outstandingReport" class="loading-state">Loading...</div>

          <div v-if="outstandingReport" class="summary-grid">
            <div class="summary-card highlight">
              <div class="summary-label">Total Outstanding</div>
              <div class="summary-value">{{ formatCurrency(outstandingReport.total_outstanding) }}</div>
            </div>
            <div class="summary-card">
              <div class="summary-label">Current</div>
              <div class="summary-value">{{ formatCurrency(outstandingReport.current) }}</div>
            </div>
            <div class="summary-card aging-warning">
              <div class="summary-label">0-30 Days</div>
              <div class="summary-value">{{ formatCurrency(outstandingReport.days_0_to_30) }}</div>
            </div>
            <div class="summary-card aging-warning">
              <div class="summary-label">30-60 Days</div>
              <div class="summary-value">{{ formatCurrency(outstandingReport.days_30_to_60) }}</div>
            </div>
            <div class="summary-card aging-danger">
              <div class="summary-label">60-90 Days</div>
              <div class="summary-value">{{ formatCurrency(outstandingReport.days_60_to_90) }}</div>
            </div>
            <div class="summary-card aging-danger">
              <div class="summary-label">90+ Days</div>
              <div class="summary-value">{{ formatCurrency(outstandingReport.days_90_plus) }}</div>
            </div>
          </div>

          <IntercomTable
            v-if="outstandingReport && outstandingReport.invoices && outstandingReport.invoices.length > 0"
            :columns="outstandingColumns"
            :data="outstandingReport.invoices"
            class="outstanding-table"
          />
          <div v-else-if="outstandingReport && (!outstandingReport.invoices || outstandingReport.invoices.length === 0)" class="empty-state">
            No outstanding invoices
          </div>
        </IntercomCard>
      </div>

      <!-- Payables Tab -->
      <div v-if="activeTab === 'payables'">
        <IntercomCard>
          <div class="report-controls">
            <IntercomButton variant="primary" :loading="isTabLoading('payables')" @click="fetchPayables">
              Refresh
            </IntercomButton>
          </div>

          <div v-if="isTabLoading('payables') && !payablesReport" class="loading-state">Loading...</div>

          <div v-if="payablesReport" class="summary-grid">
            <div class="summary-card highlight">
              <div class="summary-label">Total Payable</div>
              <div class="summary-value">{{ formatCurrency(payablesReport.total_payable) }}</div>
            </div>
            <div class="summary-card">
              <div class="summary-label">Current</div>
              <div class="summary-value">{{ formatCurrency(payablesReport.current) }}</div>
            </div>
            <div class="summary-card aging-warning">
              <div class="summary-label">0-30 Days</div>
              <div class="summary-value">{{ formatCurrency(payablesReport.days_0_to_30) }}</div>
            </div>
            <div class="summary-card aging-warning">
              <div class="summary-label">30-60 Days</div>
              <div class="summary-value">{{ formatCurrency(payablesReport.days_30_to_60) }}</div>
            </div>
            <div class="summary-card aging-danger">
              <div class="summary-label">60-90 Days</div>
              <div class="summary-value">{{ formatCurrency(payablesReport.days_60_to_90) }}</div>
            </div>
            <div class="summary-card aging-danger">
              <div class="summary-label">90+ Days</div>
              <div class="summary-value">{{ formatCurrency(payablesReport.days_90_plus) }}</div>
            </div>
          </div>

          <IntercomTable
            v-if="payablesReport && payablesReport.bills && payablesReport.bills.length > 0"
            :columns="payableColumns"
            :data="payablesReport.bills"
          />
          <div v-else-if="payablesReport && (!payablesReport.bills || payablesReport.bills.length === 0)" class="empty-state">
            No outstanding purchase bills
          </div>
        </IntercomCard>
      </div>

      <!-- GST Summary Tab -->
      <div v-if="activeTab === 'gst-summary'">
        <IntercomCard>
          <div class="report-controls">
            <div class="date-range">
              <label>From</label>
              <IntercomDateInput v-model="fromDate" />
              <label>To</label>
              <IntercomDateInput v-model="toDate" />
              <IntercomButton variant="primary" :loading="isTabLoading('gst-summary')" @click="fetchGSTSummary">
                Generate
              </IntercomButton>
            </div>
          </div>

          <div v-if="isTabLoading('gst-summary') && !gstSummary" class="loading-state">Loading...</div>

          <div v-if="gstSummary" class="summary-grid">
            <div class="summary-card">
              <div class="summary-label">Taxable Amount</div>
              <div class="summary-value">{{ formatCurrency(gstSummary.total_taxable_amount) }}</div>
            </div>
            <div class="summary-card">
              <div class="summary-label">Total CGST</div>
              <div class="summary-value cgst">{{ formatCurrency(gstSummary.total_cgst) }}</div>
            </div>
            <div class="summary-card">
              <div class="summary-label">Total SGST</div>
              <div class="summary-value sgst">{{ formatCurrency(gstSummary.total_sgst) }}</div>
            </div>
            <div class="summary-card" v-if="gstSummary.total_igst > 0">
              <div class="summary-label">Total IGST</div>
              <div class="summary-value igst">{{ formatCurrency(gstSummary.total_igst) }}</div>
            </div>
            <div class="summary-card highlight">
              <div class="summary-label">Total Tax</div>
              <div class="summary-value">{{ formatCurrency(gstSummary.total_tax) }}</div>
            </div>
            <div class="summary-card">
              <div class="summary-label">Invoices</div>
              <div class="summary-value">{{ gstSummary.invoice_count }}</div>
            </div>
          </div>
        </IntercomCard>
      </div>

      <!-- HSN Summary Tab -->
      <div v-if="activeTab === 'hsn-summary'">
        <IntercomCard>
          <div class="report-controls">
            <div class="date-range">
              <label>From</label>
              <IntercomDateInput v-model="fromDate" />
              <label>To</label>
              <IntercomDateInput v-model="toDate" />
              <IntercomButton variant="primary" :loading="isTabLoading('hsn-summary')" @click="fetchHSNSummary">
                Generate
              </IntercomButton>
            </div>
          </div>

          <div v-if="isTabLoading('hsn-summary') && hsnRows.length === 0" class="loading-state">Loading...</div>
          <IntercomTable
            v-else-if="hsnRows.length > 0"
            :columns="hsnColumns"
            :data="hsnRows"
          />
          <div v-else-if="fetchedTabs.has('hsn-summary')" class="empty-state">
            No HSN data in this period
          </div>
        </IntercomCard>
      </div>

      <!-- GSTR-3B Filing Tab -->
      <div v-if="activeTab === 'gst-filing'">
        <IntercomCard>
          <div class="report-controls">
            <div class="date-range">
              <label>Month</label>
              <select v-model="filingMonth" class="date-input">
                <option v-for="m in months" :key="m.value" :value="m.value">{{ m.label }}</option>
              </select>
              <label>Year</label>
              <select v-model="filingYear" class="date-input">
                <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
              </select>
              <IntercomButton variant="primary" :loading="isTabLoading('gst-filing')" @click="fetchGSTFiling">
                Generate
              </IntercomButton>
            </div>
          </div>

          <div v-if="isTabLoading('gst-filing') && !gstFiling" class="loading-state">Loading...</div>

          <div v-if="gstFiling" class="filing-summary">
            <div class="filing-header">
              <h3>GSTR-3B Summary -- {{ filingPeriodLabel }}</h3>
              <p class="filing-subtitle">Outward Supplies and Tax Payable</p>
            </div>
            <table class="filing-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th class="amt-col">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Total Taxable Value</td>
                  <td class="amt-col">{{ formatCurrency(gstFiling.taxable_amount) }}</td>
                </tr>
                <tr>
                  <td>ADD: Central Tax (CGST)</td>
                  <td class="amt-col">{{ formatCurrency(gstFiling.cgst_payable) }}</td>
                </tr>
                <tr>
                  <td>ADD: State/UT Tax (SGST)</td>
                  <td class="amt-col">{{ formatCurrency(gstFiling.sgst_payable) }}</td>
                </tr>
                <tr v-if="gstFiling.igst_payable > 0">
                  <td>ADD: Integrated Tax (IGST)</td>
                  <td class="amt-col">{{ formatCurrency(gstFiling.igst_payable) }}</td>
                </tr>
                <tr class="total-row">
                  <td><strong>Total Tax Payable</strong></td>
                  <td class="amt-col"><strong>{{ formatCurrency(gstFiling.total_tax_payable) }}</strong></td>
                </tr>
              </tbody>
            </table>
            <p class="filing-note">Based on {{ gstFiling.invoice_count }} finalized invoice(s)</p>
          </div>
        </IntercomCard>
      </div>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuth } from '@shared/composables/useAuth.js'
import { useBilling } from '../composables/useBilling.js'
import {
  IntercomLayout,
  IntercomTopbar,
  IntercomSidebar,
  IntercomCard,
  IntercomTable,
  IntercomButton,
  IntercomDateInput,
} from '@shared/components/intercom'
import { billingNavSections } from '../config/navigation.js'

const router = useRouter()
const { user, logout } = useAuth()
const {
  getSalesSummary,
  getProductSales,
  getCustomerSales,
  getDailySales,
  getOutstandingReport,
  getPurchaseSummary,
  getVendorPurchases,
  getProductPurchases,
  getDailyPurchases,
  getPayablesReport,
  getGSTSummary,
  getHSNSummary,
  getGSTFiling,
  getBillingSettings,
  businessName,
  fetchBusinessName,
} = useBilling()

// Billing settings (controls due date / overdue visibility)
const billingSettings = ref({})

const navSections = billingNavSections

const handleLogout = async () => {
  try {
    await logout()
    router.push('/login')
  } catch (error) {
    console.error('Logout error:', error)
    router.push('/login')
  }
}

// -- Tabs --
const salesTabs = [
  { id: 'sales-summary', label: 'Sales Summary' },
  { id: 'product-sales', label: 'Product Sales' },
  { id: 'customer-sales', label: 'Customer Sales' },
  { id: 'daily-sales', label: 'Daily Sales' },
]

const purchaseTabs = [
  { id: 'purchase-summary', label: 'Purchase Summary' },
  { id: 'vendor-purchases', label: 'Vendor Purchases' },
  { id: 'product-purchases', label: 'Product Purchases' },
  { id: 'daily-purchases', label: 'Daily Purchases' },
]

const gstTabs = [
  { id: 'gst-summary', label: 'GST Summary' },
  { id: 'hsn-summary', label: 'HSN Summary' },
  { id: 'gst-filing', label: 'GSTR-3B Filing' },
]

const activeTab = ref('sales-summary')

// -- Date state --
const today = new Date().toISOString().slice(0, 10)
const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10)
const fromDate = ref(firstOfMonth)
const toDate = ref(today)

// -- Filing tab state --
const now = new Date()
const filingMonth = ref(now.getMonth() + 1)
const filingYear = ref(now.getFullYear())

const months = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' },
  { value: 3, label: 'March' }, { value: 4, label: 'April' },
  { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' },
  { value: 9, label: 'September' }, { value: 10, label: 'October' },
  { value: 11, label: 'November' }, { value: 12, label: 'December' },
]
const years = Array.from({ length: 5 }, (_, i) => now.getFullYear() - i)

const filingPeriodLabel = computed(() => {
  const m = months.find((x) => x.value === filingMonth.value)
  return `${m?.label} ${filingYear.value}`
})

// -- Per-tab loading state (prevents spinner bleeding across tabs) --
const loading = ref(false)
const tabLoading = ref({})
const isTabLoading = (tabId) => tabLoading.value[tabId] || false

// -- Data state --
const salesSummary = ref(null)
const productSalesRows = ref([])
const customerSalesRows = ref([])
const dailySalesRows = ref([])
const outstandingReport = ref(null)
const gstSummary = ref(null)
const hsnRows = ref([])
const gstFiling = ref(null)
const purchaseSummary = ref(null)
const vendorPurchaseRows = ref([])
const productPurchaseRows = ref([])
const dailyPurchaseRows = ref([])
const payablesReport = ref(null)

// Track which tabs have been fetched (avoid re-fetching on every tab switch)
const fetchedTabs = ref(new Set())

// -- Table columns --
const paymentBreakdownColumns = [
  { key: 'payment_mode', title: 'Payment Mode' },
  { key: 'count', title: 'Count', align: 'right' },
  { key: 'amount', title: 'Amount', align: 'right', format: (v) => formatCurrency(v) },
]

const productSalesColumns = [
  { key: 'product_name', title: 'Product', width: '250px' },
  { key: 'sku', title: 'SKU', width: '120px' },
  { key: 'total_quantity', title: 'Qty Sold', align: 'right', width: '100px' },
  { key: 'total_revenue', title: 'Revenue', align: 'right', format: (v) => formatCurrency(v) },
  { key: 'total_tax', title: 'Tax', align: 'right', format: (v) => formatCurrency(v) },
]

const customerSalesColumns = [
  { key: 'customer_name', title: 'Customer', width: '250px' },
  { key: 'phone', title: 'Phone', width: '150px' },
  { key: 'invoice_count', title: 'Invoices', align: 'right', width: '100px' },
  { key: 'total_amount', title: 'Total Amount', align: 'right', format: (v) => formatCurrency(v) },
]

const dailySalesColumns = [
  { key: 'date', title: 'Date', width: '150px' },
  { key: 'invoice_count', title: 'Invoices', align: 'right', width: '100px' },
  { key: 'total_sales', title: 'Sales', align: 'right', format: (v) => formatCurrency(v) },
  { key: 'total_tax', title: 'Tax', align: 'right', format: (v) => formatCurrency(v) },
]

const outstandingColumns = computed(() => {
  const cols = [
    { key: 'invoice_number', title: 'Invoice #', width: '140px' },
    { key: 'customer_name', title: 'Customer', width: '200px' },
    { key: 'invoice_date', title: 'Date', width: '120px' },
    { key: 'total_amount', title: 'Total', align: 'right', format: (v) => formatCurrency(v) },
    { key: 'amount_paid', title: 'Paid', align: 'right', format: (v) => formatCurrency(v) },
    { key: 'amount_due', title: 'Due', align: 'right', format: (v) => formatCurrency(v) },
  ]
  if (billingSettings.value?.show_due_date !== false) {
    cols.push({ key: 'days_overdue', title: 'Days Overdue', align: 'right', width: '120px' })
  }
  return cols
})

const hsnColumns = [
  { key: 'hsn_code', title: 'HSN Code' },
  { key: 'total_quantity', title: 'Qty', align: 'right' },
  { key: 'taxable_value', title: 'Taxable Value', align: 'right', format: (v) => formatCurrency(v) },
  { key: 'cgst_amount', title: 'CGST', align: 'right', format: (v) => formatCurrency(v) },
  { key: 'sgst_amount', title: 'SGST', align: 'right', format: (v) => formatCurrency(v) },
  { key: 'igst_amount', title: 'IGST', align: 'right', format: (v) => formatCurrency(v) },
  { key: 'total_tax', title: 'Total Tax', align: 'right', format: (v) => formatCurrency(v) },
]

const vendorPurchaseColumns = [
  { key: 'vendor_name', title: 'Vendor', width: '250px' },
  { key: 'phone', title: 'Phone', width: '150px' },
  { key: 'bill_count', title: 'Bills', align: 'right', width: '100px' },
  { key: 'total_amount', title: 'Total Amount', align: 'right', format: (v) => formatCurrency(v) },
]

const productPurchaseColumns = [
  { key: 'product_name', title: 'Product', width: '250px' },
  { key: 'total_quantity', title: 'Qty Purchased', align: 'right', width: '120px' },
  { key: 'total_amount', title: 'Amount', align: 'right', format: (v) => formatCurrency(v) },
  { key: 'total_tax', title: 'Tax', align: 'right', format: (v) => formatCurrency(v) },
]

const dailyPurchaseColumns = [
  { key: 'date', title: 'Date', width: '150px' },
  { key: 'bill_count', title: 'Bills', align: 'right', width: '100px' },
  { key: 'total_amount', title: 'Amount', align: 'right', format: (v) => formatCurrency(v) },
  { key: 'total_tax', title: 'Tax', align: 'right', format: (v) => formatCurrency(v) },
]

const payableColumns = computed(() => {
  const cols = [
    { key: 'bill_number', title: 'Bill #', width: '140px' },
    { key: 'vendor_name', title: 'Vendor', width: '200px' },
    { key: 'bill_date', title: 'Date', width: '120px' },
    { key: 'total_amount', title: 'Total', align: 'right', format: (v) => formatCurrency(v) },
    { key: 'amount_paid', title: 'Paid', align: 'right', format: (v) => formatCurrency(v) },
    { key: 'amount_due', title: 'Due', align: 'right', format: (v) => formatCurrency(v) },
  ]
  if (billingSettings.value?.show_due_date !== false) {
    cols.push({ key: 'days_overdue', title: 'Days Overdue', align: 'right', width: '120px' })
  }
  return cols
})

// -- Scoped fetch helper: sets per-tab loading, marks tab as fetched --
const withTabLoading = async (tabId, fn) => {
  tabLoading.value = { ...tabLoading.value, [tabId]: true }
  loading.value = true
  try {
    await fn()
    fetchedTabs.value.add(tabId)
  } catch (error) {
    console.error(`Failed to fetch ${tabId}:`, error)
  } finally {
    tabLoading.value = { ...tabLoading.value, [tabId]: false }
    loading.value = false
  }
}

// -- API calls (each scoped to its own tab) --
const fetchSalesSummary = () => withTabLoading('sales-summary', async () => {
  salesSummary.value = await getSalesSummary(fromDate.value, toDate.value)
})

const fetchProductSales = () => withTabLoading('product-sales', async () => {
  const data = await getProductSales(fromDate.value, toDate.value)
  productSalesRows.value = Array.isArray(data) ? data : []
})

const fetchCustomerSales = () => withTabLoading('customer-sales', async () => {
  const data = await getCustomerSales(fromDate.value, toDate.value)
  customerSalesRows.value = Array.isArray(data) ? data : []
})

const fetchDailySales = () => withTabLoading('daily-sales', async () => {
  const data = await getDailySales(fromDate.value, toDate.value)
  dailySalesRows.value = Array.isArray(data) ? data : []
})

const fetchOutstanding = () => withTabLoading('outstanding', async () => {
  outstandingReport.value = await getOutstandingReport()
})

const fetchPurchaseSummary = () => withTabLoading('purchase-summary', async () => {
  purchaseSummary.value = await getPurchaseSummary(fromDate.value, toDate.value)
})

const fetchVendorPurchases = () => withTabLoading('vendor-purchases', async () => {
  const data = await getVendorPurchases(fromDate.value, toDate.value)
  vendorPurchaseRows.value = Array.isArray(data) ? data : []
})

const fetchProductPurchases = () => withTabLoading('product-purchases', async () => {
  const data = await getProductPurchases(fromDate.value, toDate.value)
  productPurchaseRows.value = Array.isArray(data) ? data : []
})

const fetchDailyPurchases = () => withTabLoading('daily-purchases', async () => {
  const data = await getDailyPurchases(fromDate.value, toDate.value)
  dailyPurchaseRows.value = Array.isArray(data) ? data : []
})

const fetchPayables = () => withTabLoading('payables', async () => {
  payablesReport.value = await getPayablesReport()
})

const fetchGSTSummary = () => withTabLoading('gst-summary', async () => {
  gstSummary.value = await getGSTSummary(fromDate.value, toDate.value)
})

const fetchHSNSummary = () => withTabLoading('hsn-summary', async () => {
  const data = await getHSNSummary(fromDate.value, toDate.value)
  hsnRows.value = Array.isArray(data) ? data : []
})

const fetchGSTFiling = () => withTabLoading('gst-filing', async () => {
  gstFiling.value = await getGSTFiling(filingYear.value, filingMonth.value)
})

// -- Tab → fetch mapping --
const tabFetchers = {
  'sales-summary': fetchSalesSummary,
  'product-sales': fetchProductSales,
  'customer-sales': fetchCustomerSales,
  'daily-sales': fetchDailySales,
  'purchase-summary': fetchPurchaseSummary,
  'vendor-purchases': fetchVendorPurchases,
  'product-purchases': fetchProductPurchases,
  'daily-purchases': fetchDailyPurchases,
  'outstanding': fetchOutstanding,
  'payables': fetchPayables,
  'gst-summary': fetchGSTSummary,
  'hsn-summary': fetchHSNSummary,
  'gst-filing': fetchGSTFiling,
}

// Auto-fetch when switching tabs (only if not already fetched)
watch(activeTab, (tabId) => {
  if (!fetchedTabs.value.has(tabId) && tabFetchers[tabId]) {
    tabFetchers[tabId]()
  }
})

// Auto-load the default tab on mount
onMounted(async () => {
  fetchBusinessName()
  // Fetch billing settings to control overdue column visibility
  try {
    const s = await getBillingSettings()
    billingSettings.value = s?.data || s || {}
  } catch { /* defaults apply */ }
  fetchSalesSummary()
})

// -- Helpers --
const formatCurrency = (val) => {
  if (val == null) return '--'
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(val)
}
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';
@import '@shared/styles/intercom-base.css';

.reports-view {
  padding: var(--space-8);
  max-width: var(--content-max-width);
  margin: 0 auto;
}

/* Page Header */
.page-header {
  margin-bottom: var(--space-6);
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  letter-spacing: var(--letter-spacing-tight);
  margin: 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
  margin-bottom: 0;
}

/* Tab Groups */
.tab-groups {
  display: flex;
  gap: var(--space-6);
  margin-bottom: var(--space-5);
  border-bottom: 2px solid var(--color-border, #e5e7eb);
  flex-wrap: wrap;
}

.tab-group {
  display: flex;
  align-items: center;
  gap: var(--space-1);
}

.tab-group-label {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-400);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-right: var(--space-1);
}

.report-tabs {
  display: flex;
  gap: 2px;
}

.tab-btn {
  padding: var(--space-2) var(--space-3);
  border: none;
  background: none;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-500);
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  transition: color 0.15s, border-color 0.15s;
  white-space: nowrap;
}

.tab-btn.active {
  color: var(--color-indigo-600, #4f46e5);
  border-bottom-color: var(--color-indigo-600, #4f46e5);
}

.tab-btn:hover:not(.active) {
  color: var(--color-charcoal);
}

/* Controls */
.report-controls {
  padding: var(--space-4) 0 var(--space-5);
  border-bottom: 1px solid var(--color-border, #e5e7eb);
  margin-bottom: var(--space-5);
}

.date-range {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  flex-wrap: wrap;
}

.date-range label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-500);
}

.date-input {
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: var(--radius-md, 6px);
  padding: var(--space-1-5, 6px) var(--space-2-5, 10px);
  font-size: var(--font-size-sm);
  color: var(--color-charcoal);
  background: var(--color-white, #fff);
}

/* Summary Grid */
.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: var(--space-4);
  padding: var(--space-2) 0;
}

.summary-card {
  background: var(--color-gray-50, #f9fafb);
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: var(--radius-lg, 8px);
  padding: var(--space-4);
}

.summary-card.highlight {
  background: var(--color-indigo-50, #eef2ff);
  border-color: var(--color-indigo-300, #a5b4fc);
}

.summary-card.aging-warning {
  background: #fffbeb;
  border-color: #fbbf24;
}

.summary-card.aging-danger {
  background: #fef2f2;
  border-color: #f87171;
}

.summary-label {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-500);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: var(--space-2);
}

.summary-value {
  font-size: var(--font-size-xl, 20px);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
}

.summary-value.cgst { color: #2563eb; }
.summary-value.sgst { color: #7c3aed; }
.summary-value.igst { color: #0891b2; }

/* Section title */
.section-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: var(--space-6) 0 var(--space-3);
}

.payment-breakdown {
  margin-top: var(--space-4);
}

/* Filing table */
.filing-summary { padding: var(--space-2) 0; }
.filing-header { margin-bottom: var(--space-4); }
.filing-header h3 {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0 0 var(--space-1);
}
.filing-subtitle {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
  margin: 0;
}

.filing-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-sm);
}

.filing-table th {
  text-align: left;
  padding: var(--space-2-5, 10px) var(--space-3);
  background: var(--color-gray-50, #f9fafb);
  border-bottom: 2px solid var(--color-border, #e5e7eb);
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-500);
  font-size: var(--font-size-xs);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.filing-table td {
  padding: var(--space-2-5, 10px) var(--space-3);
  border-bottom: 1px solid var(--color-border, #e5e7eb);
  color: var(--color-charcoal);
}

.filing-table .total-row td {
  background: var(--color-gray-50, #f9fafb);
  font-size: var(--font-size-base);
}

.amt-col {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.filing-note {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
  margin-top: var(--space-3);
}

/* Loading & Empty state */
.loading-state,
.empty-state {
  text-align: center;
  padding: var(--space-10, 40px) var(--space-8);
  color: var(--color-gray-500);
  font-size: var(--font-size-sm);
}

/* Responsive */
@media (max-width: 768px) {
  .reports-view { padding: var(--space-4); }
  .summary-grid { grid-template-columns: repeat(2, 1fr); }
  .date-range { gap: var(--space-2); }
  .tab-groups { gap: var(--space-3); }
}
</style>
